import { faTriangle } from '@fortawesome/pro-regular-svg-icons'
import { faCalendarWeek, faClock, faDice, faGrid, faUsers } from '@fortawesome/pro-solid-svg-icons'
import { DateTime } from 'luxon'

import {
  PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_SLUG,
  PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_SLUG,
  PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG,
} from '@/queries/constants'
import type {
  ProgramDetails,
  ProgramFactoid,
  ProgramIteration,
  ProgramMarketingConfig,
  ProgramOfferingTemplate,
} from '@/client/services/programs/types'
import { getPublicUrl } from '@/client/services/url/index'
import {
  PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
  PRE_CALCULUS_WORLD_SLUG,
} from '@/client/services/worlds/constants'

import {
  KA_LEARNING_LABS_PROGRAM_LEARNER_BENEFITS,
  KA_LEARNING_LABS_PROGRAM_TUTOR_BENEFITS,
  KA_LEARNING_LABS_SIGN_UP_CARD_LEARNER,
  KA_LEARNING_LABS_SIGN_UP_CARD_TUTOR,
  KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_LEARNER,
  KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_TUTOR,
} from '../constants'
import type { PreCalculusLearningLabsIterationSlug } from '../types'

const WHAT_YOULL_DO_ICONS_COLOR = 'purple.solid'

export const PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG,
  title: 'Trigonometry',
  numWeeks: 4,
  sessionFrequency: { times: 2, per: 'week' },
  sessionLength: 75,
  imageUrl: getPublicUrl('pre-calculus-learning-labs/pre-calculus-learning-labs-trigonometry.svg'),
  // these are intentionally left blank
  learnerDescription: '',
  learnerDescriptionLong: '',
  wizardIcon: faTriangle,
  whatYoullDo: {
    bulletsLearner: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_LEARNER,
    bulletsTutor: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_TUTOR,
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
  },
}

export const PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_SLUG,
  title: 'Matrices',
  numWeeks: 4,
  sessionFrequency: { times: 2, per: 'week' },
  sessionLength: 75,
  imageUrl: getPublicUrl('pre-calculus-learning-labs/pre-calculus-learning-labs-matrices.svg'),
  // these are intentionally left blank
  learnerDescription: '',
  learnerDescriptionLong: '',
  wizardIcon: faGrid,
  whatYoullDo: {
    bulletsLearner: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_LEARNER,
    bulletsTutor: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_TUTOR,
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
  },
}

export const PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_TEMPLATE: ProgramOfferingTemplate =
  {
    offeringSlug: PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_SLUG,
    title: 'Probability and Combinatorics',
    numWeeks: 4,
    sessionFrequency: { times: 2, per: 'week' },
    sessionLength: 75,
    imageUrl: getPublicUrl(
      'pre-calculus-learning-labs/pre-calculus-learning-labs-probability-combinatorics.svg'
    ),
    // these are intentionally left blank
    learnerDescription: '',
    learnerDescriptionLong: '',
    wizardIcon: faDice,
    whatYoullDo: {
      bulletsLearner: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_LEARNER,
      bulletsTutor: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_TUTOR,
      iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
    },
  }

export const PRE_CALCULUS_LEARNING_LABS_ITERATIONS: Record<
  PreCalculusLearningLabsIterationSlug,
  ProgramIteration
> = {
  'november-2024': {
    iterationSlug: 'november-2024',
    title: 'November',
    learnerRegistrationOpens: DateTime.local(2024, 10, 28, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    // TODO [confirm with matt]: this doesn't match the programs calendar, but matches the september convention of going until series start
    learnerRegistrationDeadline: DateTime.local(2024, 11, 7, 23, 59, {
      zone: 'America/New_York',
    }),
    tutorRegistrationOpens: DateTime.local(2024, 10, 14, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    tutorRegistrationDeadline: DateTime.local(2024, 10, 28, 23, 59, {
      zone: 'America/New_York',
    }),
    startDate: DateTime.local(2024, 11, 8, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2024, 12, 5, 23, 59, {
      zone: 'America/New_York',
    }),
    offerings: [
      {
        offeringTemplate: PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 11, 8, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 12, 5, 0, 0, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit2-2024-11',
      },
      {
        offeringTemplate: PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 11, 8, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 12, 5, 0, 0, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit7-2024-11',
      },
      {
        offeringTemplate:
          PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 11, 8, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 12, 5, 0, 0, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit8-2024-11',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG,
        ratio: 0.45,
      },
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_SLUG,
        ratio: 0.2,
      },
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_SLUG,
        ratio: 0.35,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/OQ1bMRnT',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/U47uXzg0',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/thpeZl38',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/uVelrRJ3',
  },
  'february-2025': {
    iterationSlug: 'february-2025',
    title: 'February',
    learnerRegistrationOpens: DateTime.local(2025, 1, 23, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 2, 6, 23, 59, { zone: 'America/New_York' }),
    tutorRegistrationOpens: DateTime.local(2025, 1, 13, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 2, 3, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 2, 7, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 3, 6, 23, 59, { zone: 'America/New_York' }),
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/tceepEbD',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/dXzaWj1q',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/ML9LMaYD',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/lknwDyLQ',
    offerings: [
      {
        offeringTemplate: PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 2, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 3, 6, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit2-2025-02',
      },
      {
        offeringTemplate: PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 2, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 2, 27, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit7-2025-02',
      },
      {
        offeringTemplate:
          PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 2, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 3, 6, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit8-2025-02',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG,
        ratio: 0.45,
      },
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_SLUG,
        ratio: 0.2,
      },
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_SLUG,
        ratio: 0.35,
      },
    ],
  },
  'march-2025': {
    iterationSlug: 'march-2025',
    title: 'March',
    learnerRegistrationOpens: DateTime.local(2025, 2, 20, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 3, 6, 23, 59, { zone: 'America/New_York' }),
    tutorRegistrationOpens: DateTime.local(2025, 2, 10, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 2, 24, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 3, 7, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 4, 3, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 3, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 4, 3, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit2-2025-03',
      },
      {
        offeringTemplate: PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 3, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 4, 3, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit7-2025-03',
      },
      {
        offeringTemplate:
          PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 3, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 4, 3, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit8-2025-03',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG,
        ratio: 0.45,
      },
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_SLUG,
        ratio: 0.2,
      },
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_SLUG,
        ratio: 0.35,
      },
    ],
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/NYrtl2G4',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/qTuey6sO',
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/Wv8JVnPj',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/g94TWDCC',
  },
  'april-2025': {
    iterationSlug: 'april-2025',
    title: 'April',
    learnerRegistrationOpens: DateTime.local(2025, 3, 19, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 4, 3, 23, 59, { zone: 'America/New_York' }),
    tutorRegistrationOpens: DateTime.local(2025, 3, 10, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 3, 24, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 4, 4, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 5, 1, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 4, 4, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 5, 1, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit2-2025-04',
      },
      {
        offeringTemplate: PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 4, 4, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 5, 1, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit7-2025-04',
      },
      {
        offeringTemplate:
          PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 4, 4, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 5, 1, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-precalc-unit8-2025-04',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG,
        ratio: 0.45,
      },
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_SLUG,
        ratio: 0.2,
      },
      {
        offeringSlug: PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_SLUG,
        ratio: 0.35,
      },
    ],
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/cCbHoymx',
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/tPO3kdkN',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/zlngQcWk',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/Ge05agde',
  },
}

export const PRE_CALCULUS_LEARNING_LABS_PROGRAM_FACTOIDS: ProgramFactoid[] = [
  {
    key: 'program-duration',
    text: '4 weeks',
    subtext: '2 sessions/week',
    iconProps: { size: 'md', color: 'purple.solid', faIcon: faCalendarWeek },
  },
  {
    key: 'session-duration',
    text: '75 minutes',
    subtext: 'Per session',
    iconProps: { size: 'md', color: 'purple.solid', faIcon: faClock },
  },
  {
    key: 'max-session-capacity',
    text: '10 learners',
    subtext: 'Max. class size',
    iconProps: { size: 'md', color: 'purple.solid', faIcon: faUsers },
  },
]

export const PRE_CALCULUS_LEARNING_LABS_MARKETING_CONFIG: ProgramMarketingConfig = {
  header: {
    learnerText: 'Master Khan Academy content one skill at a time',
    learnerSubtext: 'Stuck on a concept? Lean on a peer tutor to continue your learning',
    tutorText: 'Help students master Khan Academy content one skill at a time',
    backgroundImageUrl:
      'pre-calculus-learning-labs/pre-calculus-learning-labs-header-background.svg',
    backgroundColor: 'purple.solid',
    factoids: PRE_CALCULUS_LEARNING_LABS_PROGRAM_FACTOIDS,
    partnerships: ['KhanAcademy'],
  },
  whatYoullDo: {
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
    bulletsLearner: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_LEARNER,
    bulletsTutor: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_TUTOR,
  },
  tutorSignUpCard: KA_LEARNING_LABS_SIGN_UP_CARD_TUTOR,
  learnerSignUpCard: KA_LEARNING_LABS_SIGN_UP_CARD_LEARNER,
  tutorBenefits: KA_LEARNING_LABS_PROGRAM_TUTOR_BENEFITS,
  learnerBenefits: KA_LEARNING_LABS_PROGRAM_LEARNER_BENEFITS,
  programFactoids: PRE_CALCULUS_LEARNING_LABS_PROGRAM_FACTOIDS,
}

export const PRE_CALCULUS_LEARNING_LABS_PROGRAM_DETAILS: ProgramDetails = {
  slug: 'pre-calculus-learning-labs',
  title: 'Pre-Calculus Learning Labs',
  description: 'Master full units of Khan Academy math',
  worldSlug: PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
  fallbackWorldSlug: PRE_CALCULUS_WORLD_SLUG,
  programType: 'learning lab',
  learnerWaitlistName: 'learning-labs-pre-calculus-learner',
  tutorWaitlistName: 'learning-labs-pre-calculus-tutor',
}
